import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {CheckCircle, Circle} from '../../icons';
import {ClientPanelApi} from '../../../api/clientPanelApi';
import {BOOSTER_STEP_FEED, boosterPathFor} from '../ia_booster/boosterSteps';
import './FirstSteps.scss';

/**
 * Qué es cada paso: su copy y a dónde lleva cuando está pendiente.
 *
 * El ORDEN lo manda el payload (`first_steps.steps[]`), no este diccionario — acá sólo se resuelve
 * el significado de cada clave. Una clave que no esté acá NO se pinta: el backend puede sumar un
 * paso nuevo antes de que este FE se entere, y un chip con la clave cruda ("portal_shared") es peor
 * que un paso de menos.
 *
 * `lines_created` es el único sin destino: crear una línea es un modal de esta misma pantalla
 * (`AddBusinessUnitModal`, montado por el shell), no una navegación.
 *
 * La memoria va al acto Alimenta del AI Booster y NO a `/memory/setup` (card zcK00Emw): el paso se
 * da por hecho con cualquier material, no sólo con una reunión, y `setup` es la única vista de la
 * Memoria que no migró a la one-page — en la 2.1 abre la caja de la Memoria clásica debajo del
 * escenario.
 */
export const FIRST_STEP_DEFS = {
    lines_created: {
        labelKey: 'client_panel.first_steps_lines',
        labelDefault: 'Líneas de seguimiento creadas',
        isAction: true,
    },
    ga4_connected: {
        labelKey: 'client_panel.first_steps_ga4',
        labelDefault: 'Conecta GA4',
        path: (clientId) => `/client/${clientId}/conections`,
    },
    kickoff_meeting_loaded: {
        labelKey: 'client_panel.first_steps_kickoff',
        labelDefault: 'Suma memoria a este cliente',
        path: (clientId) => boosterPathFor(BOOSTER_STEP_FEED, clientId),
    },
    media_plan_linked: {
        labelKey: 'client_panel.first_steps_media_plan',
        labelDefault: 'Vincula su media plan',
        path: (clientId) => `/client/${clientId}/media-plan`,
    },
    portal_shared: {
        labelKey: 'client_panel.first_steps_portal',
        labelDefault: 'Comparte el portal',
        path: (clientId) => `/client/${clientId}/portal-config`,
    },
};

/** Los pasos del payload que este FE sabe pintar, en el orden en que vinieron. */
export function knownSteps(data) {
    return ((data && data.steps) || []).filter((step) => step && FIRST_STEP_DEFS[step.key]);
}

// Los contadores salen del payload (el backend ya sabe qué pasos APLICAN a este cliente). Si
// faltaran, se cuentan los pasos pintados en vez de mostrar "undefined/undefined".
function count(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

/**
 * Franja "Primeros pasos con {cliente}" (`panel.first_steps`), arriba de los KPIs.
 *
 * No es un checklist de onboarding de la app: son los pasos que hacen que ESTE cliente tenga datos
 * y contexto adentro de Dashbo. Por eso el backend ya resolvió qué mostrar — `visible: false`
 * cuando no queda nada pendiente o cuando el usuario la ocultó — y acá no se recalcula nada.
 *
 * "Ocultar" es optimista: la franja se va en el acto (nadie quiere esperar un request para dejar de
 * ver algo) y sólo vuelve si el POST falló, con un toast que lo explica. Sin confirmación: ocultar
 * no destruye nada y se revierte desde la ficha del cliente.
 */
export function FirstStepsStrip({data, clientId, clientName, onNewLine}) {
    const {t} = useTranslation();
    // Oculta sólo en ESTA sesión de la pantalla. La persistencia la hace el backend (el próximo
    // fetch del panel ya viene con `visible: false`).
    const [hidden, setHidden] = useState(false);

    // Mientras el agregado no llegó `data` es null: la franja no muestra skeleton. Aparecer y
    // desaparecer una barra arriba de los KPIs mueve la pantalla entera dos veces.
    if (!data || data.visible !== true || hidden) return null;

    const steps = knownSteps(data);
    if (!steps.length) return null;

    const completed = count(data.completed_count, steps.filter((step) => step.done).length);
    const total = count(data.total_count, steps.length);

    const handleHide = () => {
        setHidden(true);
        ClientPanelApi.hideFirstSteps(clientId).then((response) => {
            if (response && response.status === 'OK') return;
            // No se pudo guardar: mostrarla de nuevo es lo honesto (si no, vuelve sola al refrescar
            // y el usuario cree que Dashbo se olvidó de lo que le pidió).
            setHidden(false);
            toast.error(t('client_panel.first_steps_hide_error',
                'No pudimos ocultar los primeros pasos. Intenta de nuevo en unos segundos.'));
        });
    };

    const renderStep = (step) => {
        const def = FIRST_STEP_DEFS[step.key];
        const label = t(def.labelKey, def.labelDefault);

        if (step.done) {
            return (
                <span key={step.key} className="pnl-fs__step -done">
                    <CheckCircle size={16} weight="fill"/>
                    <span className="pnl-fs__label">{label}</span>
                </span>
            );
        }

        if (def.isAction) {
            return (
                <button key={step.key} type="button" className="pnl-fs__step -todo" onClick={onNewLine}>
                    <Circle size={16}/>
                    <span className="pnl-fs__label">{label}</span>
                </button>
            );
        }

        return (
            <Link key={step.key} className="pnl-fs__step -todo" to={def.path(clientId)}>
                <Circle size={16}/>
                <span className="pnl-fs__label">{label}</span>
            </Link>
        );
    };

    return (
        <section className="pnl-fs">
            <h2 className="pnl-fs__title">
                {clientName
                    ? t('client_panel.first_steps_title', 'Primeros pasos con {{name}}', {
                        name: clientName,
                        interpolation: {escapeValue: false},
                    })
                    : t('client_panel.first_steps_title_generic', 'Primeros pasos con este cliente')}
            </h2>

            <span className="pnl-fs__pill">{completed}/{total}</span>

            <div className="pnl-fs__steps">{steps.map(renderStep)}</div>

            <button type="button" className="pnl-fs__hide" onClick={handleHide}>
                {t('client_panel.first_steps_hide', 'Ocultar')}
            </button>
        </section>
    );
}
